import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Front-end user preferences — purely client-side toggles the console remembers
 * per browser. Persisted to `localStorage` under `icgchat-prefs` via zustand's
 * `persist` middleware (the store dir's other stores predate persistence: `ui`
 * is ephemeral selection state, `auth` hand-rolls its own localStorage sync for
 * the token; this is the first that wants the middleware's rehydrate-on-load).
 *
 *   - `notif`     — show a desktop notification on a new message.
 *   - `sound`     — play a sound on a new message.
 *   - `enterSend` — Enter sends the composer draft (Shift+Enter always inserts
 *                   a newline). `false` flips it: Enter inserts a newline and
 *                   the send button is the only way to send.
 *   - `profileOpen` — whether the customer-profile sidebar (the 4th column,
 *                   rendered INSIDE the chat panel) is showing. Defaults to
 *                   `true` (since v1, 2026-07): opening a conversation shows
 *                   the customer profile docked beside the chat; the agent can
 *                   dismiss it via the sidebar's ✕ / the chat header's person
 *                   button, and that choice is remembered.
 *   - `profileSide` — which side of the chat the profile sidebar docks on
 *                   (`'left'` / `'right'`); flipped in place from the sidebar
 *                   header's ⇤/⇥ button. Defaults to `'right'`.
 *
 * NOTE: `notif` and `sound` are STORED ONLY. There is no notification / sound
 * subsystem yet — these persist so a future PR can wire them up without a
 * migration. Do not infer a live feature from their presence here. `enterSend`,
 * `profileOpen`, and `profileSide` have behavior today (read by the chat
 * Composer / ChatPanel).
 *
 * All four booleans default to `true`. `profileOpen` originally defaulted
 * `false` (sidebar hidden until requested); persist `version: 1` flips a
 * stored v0 `false` to `true` exactly once on upgrade, so every agent sees
 * the new open-by-default layout — closing it again after that is persisted
 * as v1 state and never re-flipped.
 */
export interface PrefsState {
  notif: boolean;
  sound: boolean;
  enterSend: boolean;
  profileOpen: boolean;
  profileSide: 'left' | 'right';
  setNotif: (value: boolean) => void;
  setSound: (value: boolean) => void;
  setEnterSend: (value: boolean) => void;
  setProfileOpen: (value: boolean) => void;
  toggleProfile: () => void;
  setProfileSide: (side: 'left' | 'right') => void;
}

/** localStorage key holding the persisted preference booleans. */
export const PREFS_STORAGE_KEY = 'icgchat-prefs';

export const usePrefsStore = create<PrefsState>()(
  persist(
    (set) => ({
      notif: true,
      sound: true,
      enterSend: true,
      profileOpen: true,
      profileSide: 'right',
      setNotif: (value) => set({ notif: value }),
      setSound: (value) => set({ sound: value }),
      setEnterSend: (value) => set({ enterSend: value }),
      setProfileOpen: (value) => set({ profileOpen: value }),
      toggleProfile: () => set((state) => ({ profileOpen: !state.profileOpen })),
      setProfileSide: (side) => set({ profileSide: side }),
    }),
    {
      name: PREFS_STORAGE_KEY,
      // v0 → v1 (2026-07): the customer-profile sidebar became open by
      // default. A v0 payload predates that decision — its stored
      // `profileOpen: false` is overwhelmingly the old DEFAULT (never an
      // explicit opt-out, since v0 had nothing to opt out of), so flip it
      // once. Any close after this upgrade persists as v1 and is honored.
      version: 1,
      migrate: (persisted, version) => {
        const state = (persisted ?? {}) as Partial<PrefsState>;
        if (version < 1) {
          return { ...state, profileOpen: true };
        }
        return state;
      },
      // `window.localStorage` is referenced lazily inside the factory, which
      // `createJSONStorage` invokes in a try/catch. On a server render (no
      // `window`) the access throws, the middleware catches it and simply
      // skips persistence — so the store still initializes with the defaults
      // above rather than crashing. SSR-safe by construction.
      storage: createJSONStorage(() => window.localStorage),
      // Persist only the data, never the setters.
      partialize: (state) => ({
        notif: state.notif,
        sound: state.sound,
        enterSend: state.enterSend,
        profileOpen: state.profileOpen,
        profileSide: state.profileSide,
      }),
    },
  ),
);
